import React, { useState } from "react"
import { Crosshair } from "lucide-react"
import { parseLatLng } from "@/components/maps/coordinateParser"
import { useTranslation } from "react-i18next"

export type GoToCoordinateMenuProps = {
  /** Shared with the parent context menu so click-outside detection still works. */
  menuRef: React.RefObject<HTMLDivElement>
  position: { x: number; y: number }
  /** Called with the parsed [lng, lat] when the user submits a valid coordinate. */
  onSubmit: (lng: number, lat: number) => void
  /** Return to the main context menu. */
  onBack: () => void
}

/**
 * Submenu that accepts raw coordinates or a Google Maps link, then asks the
 * parent to zoom the map to that position and drop a marker there.
 */
const GoToCoordinateMenu: React.FC<GoToCoordinateMenuProps> = ({
  menuRef,
  position,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation()
  const [input, setInput] = useState("")
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = () => {
    const coords = parseLatLng(input)
    if (!coords) {
      setError(t("maps.goToCoordinateMenu.invalidInput"))
      return
    }
    const [lng, lat] = coords
    onSubmit(lng, lat)
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] min-w-[300px] max-w-lg overflow-hidden rounded-md bg-popover text-sm text-popover-foreground shadow-md"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <div className="p-3">
        <div className="flex items-start gap-3 text-xs font-normal">
          <div className="mt-1 flex-shrink-0">
            <Crosshair className="h-4 w-4" />
          </div>
          <div className="flex-1 space-y-1">
            <div className="font-medium">
              {t("maps.goToCoordinateMenu.title")}
            </div>
            <div className="text-muted-foreground">
              {t("maps.goToCoordinateMenu.description")}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col border-t p-1">
        <button
          className="flex items-center gap-3 rounded-sm px-3 py-2 text-xs hover:bg-accent hover:text-accent-foreground"
          onClick={onBack}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t("common.back")}
        </button>

        <div className="space-y-2 p-2">
          <input
            type="text"
            autoFocus
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                handleSubmit()
              }
            }}
            placeholder={t("maps.goToCoordinateMenu.placeholder")}
            className="w-full rounded-sm border bg-background px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-ring"
          />
          {error ? (
            <div className="text-xs text-destructive">{error}</div>
          ) : null}
          <button
            className="flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!input.trim()}
          >
            <Crosshair className="h-4 w-4" />
            {t("maps.goToCoordinateMenu.submit")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GoToCoordinateMenu
